import { supabase } from "@/integrations/supabase/client";

/**
 * Settings an anonymous visitor is allowed to read.
 *
 * Read through get_public_setting, which carries its own allowlist — the table
 * itself is not readable. Adding a key here is not enough to publish it.
 */
export type PublicSettingKey = "public_registration_enabled";

/**
 * Whether anyone may create an account from /auth without an invite.
 *
 * Defaults to false, and stays false if the lookup fails. During a private beta
 * the cost of wrongly closing the door is a visitor who joins the waitlist; the
 * cost of wrongly opening it is strangers inside the product. Approved invites
 * go through /create-account and are never gated by this.
 */
export async function isPublicRegistrationEnabled(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc("get_public_setting" as never, {
      _key: "public_registration_enabled",
    } as never);
    if (error) return false;
    return data === true;
  } catch {
    return false;
  }
}

/** Admin-only write. RLS rejects this for anyone without the admin role. */
export async function setPublicRegistrationEnabled(enabled: boolean): Promise<{ error: string | null }> {
  const { data: userData } = await supabase.auth.getUser();
  const { error } = await supabase
    .from("app_settings" as never)
    .update({
      value: enabled as never,
      updated_at: new Date().toISOString(),
      updated_by: userData?.user?.id ?? null,
    } as never)
    .eq("key", "public_registration_enabled");
  return { error: error?.message ?? null };
}
